import type { BlockDef } from '../../prop-types.ts'
import { EMPTY_TEXT } from '../../text.ts'

/** The site-scoped footer. Carries the branches, because a Saudi customer's footer is where the
 *  addresses live and a single-address footer is the shape that has to be worked around. */
export const footer: BlockDef = {
  type: 'footer',
  v: 1,
  label: { en: 'Footer', ar: 'التذييل' },
  scope: 'site',
  description:
    'The bottom of every page: a short line about the business, a few links, the branches with their addresses, and social profiles.',
  whenToUse: 'Exactly once per site. It is created with the site and appears on every page.',
  whenNotToUse: 'Not for a contact form or a map — those are the Contact block, which belongs on a page.',
  variants: [
    { value: 'columns', label: { en: 'Columns', ar: 'أعمدة' } },
    { value: 'compact', label: { en: 'One line', ar: 'سطر واحد' } },
  ],
  fields: [
    { key: 'tagline', label: { en: 'Short line', ar: 'سطر قصير' }, type: { kind: 'text' }, default: EMPTY_TEXT },
    {
      key: 'links',
      label: { en: 'Links', ar: 'الروابط' },
      type: {
        kind: 'list',
        max: 8,
        item: [
          { key: 'label', label: { en: 'Text', ar: 'النص' }, type: { kind: 'text' }, default: EMPTY_TEXT },
          { key: 'link', label: { en: 'Goes to', ar: 'ينتقل إلى' }, type: { kind: 'link' }, default: { kind: 'none' } },
        ],
      },
      default: [],
    },
    {
      key: 'branches',
      label: { en: 'Branches', ar: 'الفروع' },
      type: {
        kind: 'list',
        max: 12,
        item: [
          { key: 'name', label: { en: 'Branch', ar: 'الفرع' }, type: { kind: 'text' }, default: EMPTY_TEXT },
          { key: 'address', label: { en: 'Address', ar: 'العنوان' }, type: { kind: 'text' }, default: EMPTY_TEXT },
        ],
      },
      default: [],
    },
    {
      key: 'social',
      label: { en: 'Social profiles', ar: 'حسابات التواصل' },
      type: {
        kind: 'list',
        max: 6,
        item: [
          {
            key: 'network',
            label: { en: 'Network', ar: 'المنصّة' },
            type: {
              kind: 'enum',
              options: [
                { value: 'instagram', label: { lit: 'Instagram' } },
                { value: 'x', label: { lit: 'X' } },
                { value: 'tiktok', label: { lit: 'TikTok' } },
                { value: 'snapchat', label: { lit: 'Snapchat' } },
                { value: 'whatsapp', label: { lit: 'WhatsApp' } },
                { value: 'linkedin', label: { lit: 'LinkedIn' } },
              ],
            },
            default: 'instagram',
          },
          { key: 'link', label: { en: 'Address', ar: 'الرابط' }, type: { kind: 'link' }, default: { kind: 'none' } },
        ],
      },
      default: [],
    },
    // ⚠️ NOT A `text` FIELD WITH A YEAR TYPED INTO IT. A customer who writes "© 2026 Ahmed" has
    // published a site that is wrong on the first of January and will not notice for a year. The
    // renderer composes the notice from the business name and the current year.
    { key: 'showCopyright', label: { en: 'Show copyright line', ar: 'إظهار سطر حقوق النشر' }, type: { kind: 'boolean' }, default: true },
  ],
}
